// The parameter panel: every control in here is generated from SCHEMA, so
// adding a knob to params.js is the whole job -- nothing in this file names a
// parameter. That is the construction-set move the spec asks for: rather than
// build the right interface, build the controls to find it live.

import Nexus from 'nexusui'
import { SCHEMA } from './params.js'
import { SOUNDS } from './engine/presets.js'
import './panel.css'

// Groups we know how to order and name; anything else in SCHEMA falls in
// after these, in schema order, with a capitalised name. A new group still
// appears without an edit here.
const GROUP_ORDER = ['pads', 'cli', 'camera', 'engine', 'study']
const GROUP_LABELS = { pads: 'pads', cli: 'cli', camera: 'camera', engine: 'engine', study: 'study' }
const OPEN_BY_DEFAULT = new Set(['pads', 'engine'])

const DIAL_SIZE = 52
const SLIDER_HEIGHT = 22

const presetSound = (group) => (group.startsWith('preset.') ? group.slice(7) : null)

// Numbers in a preset group are FM voice parameters -- unitless, continuous,
// and read as a bank; dials pack them and look like the synth they are.
// Everything else is a quantity with a unit (px, ms, bpm, a count), which is
// easier to compare against its range on a horizontal slider.
const widgetKind = (group) => (presetSound(group) ? 'dial' : 'slider')

const decimalsFor = (step) => (String(step).split('.')[1] ?? '').length

function cssVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // clipboard API needs a secure context; the dev server over wifi is not one
    const ta = document.createElement('textarea')
    ta.value = text
    ta.className = 'nj-offscreen'
    document.body.appendChild(ta)
    ta.select()
    let ok = false
    try { ok = document.execCommand('copy') } catch { ok = false }
    ta.remove()
    return ok
  }
}

// '?' is the shortcut, but the CLI interface swallows every printable
// character, so the handler stands down whenever a text field has focus.
function isTyping() {
  const el = document.activeElement
  if (!el) return false
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable
}

export function createPanel({ api, params, logger, root = document.body }) {
  const el = document.createElement('aside')
  el.className = 'nj-panel'
  el.setAttribute('role', 'dialog')
  el.setAttribute('aria-label', 'parameters')
  el.hidden = true

  const fab = document.createElement('button')
  fab.className = 'nj-fab'
  fab.type = 'button'
  fab.textContent = 'params'
  fab.setAttribute('aria-expanded', 'false')

  el.innerHTML = `
    <header class="nj-head">
      <h2>parameters</h2>
      <span class="nj-key" title="keyboard shortcut">?</span>
      <span class="spacer"></span>
      <button type="button" class="nj-close" aria-label="close panel">×</button>
    </header>
    <div class="nj-body"></div>
    <footer class="nj-foot">
      <button type="button" data-act="share">copy config URL</button>
      <button type="button" data-act="reset">reset to defaults</button>
      <span class="nj-flash" aria-live="polite"></span>
    </footer>`

  const body = el.querySelector('.nj-body')
  const flashEl = el.querySelector('.nj-flash')
  root.appendChild(el)
  root.appendChild(fab)

  const controls = new Map()   // key -> { set(v), resize?(w), destroy() }
  const unsubs = []
  let built = false
  let open = false
  let syncing = false          // one guard for both directions of the binding
  let flashTimer = 0
  let statusEl = null

  function flash(msg) {
    flashEl.textContent = msg
    clearTimeout(flashTimer)
    flashTimer = setTimeout(() => { flashEl.textContent = '' }, 2400)
  }

  // --- binding ------------------------------------------------------------

  // control -> the world. Engine-bearing parameters go through the api so the
  // command log sees them; everything else is a pure params write.
  function route(key, value) {
    if (key === 'engine.tempo') return api.setTempo(value)
    if (key === 'engine.volume') {
      // TODO: the api has no master-volume command (engine.setMasterVolume
      // exists but is not exposed), so this only reaches the URL hash for now.
      return void params.set(key, value)
    }
    if (key.startsWith('preset.')) {
      const [, sound, name] = key.split('.')
      return api.setParam(sound, name, value)
    }
    if (key === 'study.participantId' && logger?.setParticipantId) {
      // the logger owns the id (it also mirrors it into params)
      return logger.setParticipantId(value)
    }
    params.set(key, value)
  }

  function pull(key, value) {
    if (syncing) return
    syncing = true
    try {
      route(key, value)
      const v = params.get(key)
      if (v !== value) controls.get(key)?.set(v)
    } finally {
      syncing = false
    }
  }

  function push(key, value) {
    if (syncing) return
    syncing = true
    try { controls.get(key)?.set(value) } finally { syncing = false }
  }

  // params.reset() only rewrites values; the engine has to be told again.
  function applyToEngine() {
    api.setTempo(params.get('engine.tempo'))
    for (const group of Object.keys(SCHEMA)) {
      const sound = presetSound(group)
      if (!sound) continue
      for (const name of Object.keys(SCHEMA[group])) {
        api.setParam(sound, name, params.get(`${group}.${name}`))
      }
    }
  }

  // --- control construction ----------------------------------------------

  function makeNumber(key, spec, host, out, kind, accent) {
    // Nexus writes a fixed pixel width onto the element it mounts in, so it
    // gets its own div inside the fluid cell -- the cell stays measurable.
    const mount = document.createElement('div')
    host.appendChild(mount)

    const size = kind === 'dial'
      ? [DIAL_SIZE, DIAL_SIZE]
      : [Math.max(80, host.clientWidth || 160), SLIDER_HEIGHT]
    const Widget = kind === 'dial' ? Nexus.Dial : Nexus.Slider
    const w = new Widget(mount, {
      size,
      min: spec.min,
      max: spec.max,
      step: spec.step ?? 0,
      value: params.get(key),
    })
    w.colorize('accent', accent)
    w.colorize('fill', cssVar('--bg-sunken'))
    w.colorize('mediumLight', cssVar('--line'))

    const dp = decimalsFor(spec.step ?? 1)
    const show = (v) => { out.textContent = Number(v).toFixed(dp) }
    show(w.value)
    w.on('change', (v) => { show(v); pull(key, v) })

    return {
      set(v) { w.value = v },
      resize: kind === 'slider'
        ? () => w.resize(Math.max(80, host.clientWidth || 160), SLIDER_HEIGHT)
        : null,
      destroy() { w.destroy(); mount.remove() },
    }
  }

  function makeBoolean(key, host, out, accent) {
    const w = new Nexus.Toggle(host, { size: [42, 22], state: !!params.get(key) })
    w.colorize('accent', accent)
    w.colorize('fill', cssVar('--bg-sunken'))
    const show = (v) => { out.textContent = v ? 'on' : 'off' }
    show(w.state)
    w.on('change', (v) => { show(v); pull(key, v) })
    return {
      set(v) { if (w.state !== !!v) w.state = !!v },
      destroy() { w.destroy() },
    }
  }

  // NexusUI has no picker worth the name, so enums and strings stay native --
  // which also gets us the platform's mobile keyboard and wheel for free.
  function makeSelect(key, spec, host) {
    const sel = document.createElement('select')
    for (const opt of spec.options) {
      const o = document.createElement('option')
      o.value = o.textContent = opt
      sel.appendChild(o)
    }
    sel.value = params.get(key)
    sel.addEventListener('change', () => pull(key, sel.value))
    host.appendChild(sel)
    return { set(v) { sel.value = v }, destroy() { sel.remove() } }
  }

  function makeText(key, host) {
    const input = document.createElement('input')
    input.type = 'text'
    input.value = params.get(key) ?? ''
    // commit on blur/enter rather than per keystroke, so a half-typed
    // participant id never reaches the logger or the URL hash
    input.addEventListener('change', () => pull(key, input.value))
    host.appendChild(input)
    return { set(v) { input.value = v ?? '' }, destroy() { input.remove() } }
  }

  function buildField(group, name, spec, wrap, accent) {
    const key = `${group}.${name}`
    const kind = widgetKind(group)
    const dial = spec.type === 'number' && kind === 'dial'

    const field = document.createElement('div')
    field.className = dial ? 'nj-field nj-field-dial' : 'nj-field'
    field.innerHTML = `<label>${spec.label ?? name}</label><div class="nj-widget"></div><output></output>`
    wrap.appendChild(field)

    const host = field.querySelector('.nj-widget')
    const out = field.querySelector('output')
    field.querySelector('label').addEventListener('click', () => host.querySelector('select,input')?.focus())

    let control
    if (spec.type === 'number') control = makeNumber(key, spec, host, out, kind, accent)
    else if (spec.type === 'boolean') control = makeBoolean(key, host, out, accent)
    else if (spec.type === 'enum') control = makeSelect(key, spec, host)
    else control = makeText(key, host)

    controls.set(key, control)
  }

  function buildStudyExtras(section) {
    const row = document.createElement('div')
    row.className = 'nj-buttons'
    row.innerHTML = `
      <button type="button" data-act="newid">new participant id</button>
      <button type="button" data-act="csv">download CSV</button>
      <button type="button" data-act="summary">copy summary line</button>`
    section.appendChild(row)

    statusEl = document.createElement('div')
    statusEl.className = 'nj-status'
    statusEl.setAttribute('aria-live', 'polite')
    section.appendChild(statusEl)

    row.addEventListener('click', async (e) => {
      const act = e.target.dataset?.act
      if (act === 'newid') flash(`id: ${logger.newParticipantId()}`)
      else if (act === 'csv') { logger.downloadCSV(); flash('CSV downloaded') }
      else if (act === 'summary') {
        const ok = await copyText(logger.summaryLine())
        flash(ok ? 'summary copied' : 'copy failed — see console')
        if (!ok) console.log(logger.summaryLine())
      }
    })

    const showStatus = (s) => {
      const bits = [
        s.participantId,
        `${s.trials} trial${s.trials === 1 ? '' : 's'}`,
        `${s.queued} queued`,
        s.endpoint ? (s.enabled ? 'endpoint on' : 'endpoint idle') : 'no endpoint',
      ]
      statusEl.textContent = bits.join(' · ')
    }
    showStatus(logger.status())
    unsubs.push(logger.onStatus(showStatus))
  }

  function buildGroup(group) {
    const sound = presetSound(group)
    const accent = sound ? cssVar(`--${sound}`) : cssVar('--accent')

    const section = document.createElement('details')
    section.className = sound ? 'nj-group nj-group-preset' : 'nj-group'
    section.style.setProperty('--group-accent', accent)
    if (OPEN_BY_DEFAULT.has(group)) section.open = true

    const summary = document.createElement('summary')
    summary.textContent = sound ? `${sound} voice` : (GROUP_LABELS[group] ?? group)
    section.appendChild(summary)

    const wrap = document.createElement('div')
    wrap.className = sound ? 'nj-fields nj-fields-dials' : 'nj-fields'
    section.appendChild(wrap)

    for (const [name, spec] of Object.entries(SCHEMA[group])) {
      if (spec.internal) continue
      buildField(group, name, spec, wrap, accent)
    }
    if (group === 'study') buildStudyExtras(section)

    // a closed <details> is display:none, so its sliders were built at the
    // fallback width; re-measure them the first time it is opened
    section.addEventListener('toggle', () => { if (section.open) relayout() })
    body.appendChild(section)
  }

  // Canvas/SVG widgets measure their host, and a host inside `display: none`
  // measures zero -- so nothing is constructed until the panel is first shown.
  function build() {
    if (built) return
    built = true

    const presetGroups = SOUNDS.map((s) => `preset.${s}`).filter((g) => SCHEMA[g])
    const known = new Set([...GROUP_ORDER, ...presetGroups])
    const rest = Object.keys(SCHEMA).filter((g) => !known.has(g))
    const visible = (g) => Object.values(SCHEMA[g]).some((spec) => !spec.internal)
    const order = [...GROUP_ORDER.filter((g) => SCHEMA[g]), ...rest, ...presetGroups].filter(visible)

    for (const group of order) buildGroup(group)
    unsubs.push(params.onChange(push))
  }

  // --- layout -------------------------------------------------------------

  let resizeFrame = 0
  function relayout() {
    cancelAnimationFrame(resizeFrame)
    resizeFrame = requestAnimationFrame(() => {
      for (const c of controls.values()) c.resize?.()
    })
  }

  const ro = new ResizeObserver(() => { if (open) relayout() })

  // --- open / close -------------------------------------------------------

  function setOpen(next) {
    if (next === open) return
    open = next
    el.hidden = !open
    fab.setAttribute('aria-expanded', String(open))
    fab.classList.toggle('on', open)
    if (!open) return
    build()
    // sliders were sized from a host that had just become visible; one more
    // pass after layout settles catches the mobile sheet's full width
    relayout()
  }

  function onKey(e) {
    if (e.key === 'Escape' && open) { setOpen(false); return }
    if (isTyping()) return
    if (e.key === '?' || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')) {
      e.preventDefault()
      setOpen(!open)
    }
  }

  el.querySelector('.nj-close').addEventListener('click', () => setOpen(false))
  fab.addEventListener('click', () => setOpen(!open))

  el.querySelector('.nj-foot').addEventListener('click', async (e) => {
    const act = e.target.dataset?.act
    if (act === 'share') {
      const ok = await copyText(params.shareUrl())
      flash(ok ? 'config URL copied' : 'copy failed — see console')
      if (!ok) console.log(params.shareUrl())
    } else if (act === 'reset') {
      params.reset()
      applyToEngine()
      flash('reset to defaults')
    }
  })

  window.addEventListener('keydown', onKey)
  ro.observe(el)

  return {
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!open),
    get isOpen() { return open },

    destroy() {
      window.removeEventListener('keydown', onKey)
      ro.disconnect()
      cancelAnimationFrame(resizeFrame)
      clearTimeout(flashTimer)
      for (const off of unsubs) off()
      unsubs.length = 0
      // Nexus removes its own element from the host, so widgets go first
      for (const c of controls.values()) c.destroy()
      controls.clear()
      el.remove()
      fab.remove()
    },
  }
}
